import { useLayoutEffect, useRef, useCallback } from 'react';
import { ACC_MOVIL, BEIGE_MOVIL, BRD_MOVIL, campoTieneValor, claseInpMovil } from './agenteCamposVinculados';

/**
 * Input/select/textarea estilo APP: beige vacío, azul #3b82f6 + texto blanco con valor.
 * data-filled + class has-value; estilos !important vía setProperty.
 * Reaplica blanco tras autofill de Chrome (animationstart).
 */
export default function InpCampoMovil({
  as = 'input',
  value,
  className = '',
  style,
  children,
  ...rest
}) {
  const filled = campoTieneValor(value);
  const inpCls = `${claseInpMovil(filled)}${className ? ` ${className}` : ''}`.trim();
  const Tag = as === 'select' ? 'select' : as === 'textarea' ? 'textarea' : 'input';
  const ref = useRef(null);
  const filledRef = useRef(filled);
  filledRef.current = filled;

  const {
    autoComplete: autoCompleteProp,
    autoCorrect,
    autoCapitalize,
    spellCheck,
    ...restProps
  } = rest;

  const paint = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const isFilled = filledRef.current;
    const bg = isFilled ? ACC_MOVIL : BEIGE_MOVIL;
    const fg = isFilled ? '#ffffff' : '#000000';
    const bd = isFilled ? ACC_MOVIL : BRD_MOVIL;
    el.style.setProperty('background', bg, 'important');
    el.style.setProperty('background-color', bg, 'important');
    el.style.setProperty('background-image', 'none', 'important');
    el.style.setProperty('color', fg, 'important');
    el.style.setProperty('-webkit-text-fill-color', fg, 'important');
    el.style.setProperty('caret-color', fg, 'important');
    el.style.setProperty('border', `2px solid ${bd}`, 'important');
    el.style.setProperty('border-color', bd, 'important');
    el.style.setProperty('color-scheme', isFilled ? 'dark' : 'light', 'important');
    el.style.setProperty('-webkit-box-shadow', `0 0 0 1000px ${bg} inset`, 'important');
    el.style.setProperty('box-shadow', `0 0 0 1000px ${bg} inset`, 'important');
  }, []);

  useLayoutEffect(() => {
    paint();
    const el = ref.current;
    if (!el) return undefined;
    // Chrome dispara animationstart al autofillar; reaplicar blanco/navy
    const onAuto = () => {
      paint();
      requestAnimationFrame(paint);
      setTimeout(paint, 0);
      setTimeout(paint, 50);
    };
    el.addEventListener('animationstart', onAuto);
    el.addEventListener('change', onAuto);
    return () => {
      el.removeEventListener('animationstart', onAuto);
      el.removeEventListener('change', onAuto);
    };
  }, [filled, value, paint]);

  const controlStyle = {
    colorScheme: filled ? 'dark' : 'light',
    width: '100%',
    fontFamily: 'inherit',
    fontSize: 15,
    lineHeight: 1.25,
    padding: Tag === 'textarea' ? '12px 14px' : '13px 14px',
    borderRadius: 12,
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    appearance: 'none',
    cursor: Tag === 'select' ? 'pointer' : undefined,
    outline: 'none',
    color: filled ? '#ffffff' : '#000000',
    WebkitTextFillColor: filled ? '#ffffff' : '#000000',
    caretColor: filled ? '#ffffff' : '#000000',
    ...(style || {}),
  };

  const antiAutofill = Tag === 'input' ? {
    autoComplete: autoCompleteProp || 'new-password',
    autoCorrect: autoCorrect ?? 'off',
    autoCapitalize: autoCapitalize ?? 'off',
    spellCheck: spellCheck ?? false,
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-form-type': 'other',
  } : {};

  const common = {
    ref,
    className: inpCls,
    value: value ?? '',
    style: controlStyle,
    'data-filled': filled ? '1' : '0',
  };

  if (Tag === 'select') {
    return (
      <select {...common} {...restProps}>
        {children}
      </select>
    );
  }
  if (Tag === 'textarea') {
    return <textarea {...common} autoComplete="off" {...restProps} />;
  }
  return <input {...common} {...restProps} {...antiAutofill} />;
}

export { campoTieneValor, claseInpMovil };
